import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { canAccessRoute } from "./ProtectedRoute";
import { Link, useNavigate } from "react-router-dom";
import { AppDispatch } from '../store';
import { useAppDispatch } from '../hooks';
import { logout } from "../slices/auth";
import { Toast, ToastContainer } from "react-bootstrap";
import { State } from "../interfaces/types";

interface SubMenuItem {
  path: string;
  icon: React.ReactNode;
  title: string;
  subNav?: SubMenuItem[];
  iconOpened?: React.ReactNode;
  iconClosed?: React.ReactNode;
  hasOnClick?: boolean;
}

interface SubMenuProps {
  item: SubMenuItem;
  onClickMenuItem: () => void;
}

const SubMenu: React.FC<SubMenuProps> = ({ item, onClickMenuItem }) => {
  let navigate = useNavigate();
  const [subnav, setSubnav] = useState<boolean>(false);
  const showSubnav = () => setSubnav(!subnav);

  const { user: currentUser } = useSelector((state: State) => state.auth);
  const dispatch: AppDispatch = useAppDispatch();

  const [toastMessage] = useState<string | null>(null);
  const [showToast, setShowToast] = useState<boolean>(false);

  const logOut = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const canShowItem = (item: SubMenuItem): boolean => {
    const roles = currentUser?.roles || [];
    if (!canAccessRoute(item.path, roles)) return false;

    switch (item.title) {
      case "Perfil":
        return roles.includes("ROLE_CLIENT");

      case "Mis Rutinas":
        return roles.includes("ROLE_CLIENT");

      case "Mis Entrenamientos":
        return roles.includes("ROLE_CLIENT");

      case "Mis Clases":
        return item.path === "/my-classes"
          ? roles.includes("ROLE_CLIENT")
          : roles.includes("ROLE_INSTRUCTOR");

      case "Mis Pagos":
        return roles.includes("ROLE_CLIENT");

      case "Registrar Cliente":
        return roles.includes("ROLE_RECEPCIONIST") || roles.includes("ROLE_COACH") || roles.includes("ROLE_ADMIN");

      case "Asignar Plan":
        return roles.includes("ROLE_RECEPCIONIST") || roles.includes("ROLE_COACH") || roles.includes("ROLE_ADMIN");

      case "Registrar Pago":
        return roles.includes("ROLE_RECEPCIONIST") || roles.includes("ROLE_COACH") || roles.includes("ROLE_ADMIN");

      case "Ejercicios":
      case "Rutinas":
        return roles.includes("ROLE_COACH") || roles.includes("ROLE_ADMIN");

      case "Administración":
        return roles.includes("ROLE_ADMIN");

      default:
        return true;
    }
  };

  const goProfile = () => {
    navigate("/profile");
  };
  
  const getOnClick: (title: string) => (e: React.MouseEvent) => void =
  (title: string) => {
    switch (title) {
      case "Salir":
        return logOut;
      case "Perfil":
        return goProfile;
      default:
        return () => {};
    }
  };

  return (
    <>
      <ToastContainer position="bottom-end" className="p-3">
        <Toast 
          onClose={() => setShowToast(false)} 
          show={showToast} 
          delay={4000} 
          autohide 
          bg="success"
          style={{ width: '350px' }}
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      {item.hasOnClick ? (
        item.title === 'Salir' ? ( <li className="nav-text">
          <Link to={item.title === "Salir" ? "/login" : item.path} onClick={getOnClick(item.title)}>
            {item.icon}
            <span className="d-sm-inline">{item.title}</span>
          </Link>
        </li>) : canShowItem(item) && (<li className="nav-text">
          <Link to={item.title === "Salir" ? "/login" : item.path} onClick={getOnClick(item.title)}>
            {item.icon}
            <span className="d-sm-inline">{item.title}</span>
          </Link>
        </li>)
      ) : canShowItem(item) && (
        <li className="nav-text">
          <Link
            to={item.path}
            onClick={item.subNav ? showSubnav : onClickMenuItem}
          >
            <div className="nav-with-sub">
              {item.icon}
              <span className="d-sm-inline">
                {item.title}
              </span>
            </div>
            {item.subNav && (
              <div className="nav-with-sub ps-1 d-sm-inline">
                {item.subNav && subnav
                  ? item.iconOpened
                  : item.subNav
                  ? item.iconClosed
                  : null}
              </div>
            )}
          </Link>
        </li>)}
      {canShowItem(item) && subnav &&
        item.subNav?.map((subItem, index) => (
          <li className="nav-text-sub" key={index}>
            <Link to={subItem.path} onClick={onClickMenuItem}>
              {subItem.icon}
              <span className="d-sm-inline">
                {subItem.title}
              </span>
            </Link>
          </li>
        ))
      }
    </>
  );
};

export default SubMenu;
